import {getAuthData} from '@utils/auth.js'
import {BUILD_VERSION} from './buildInfo.js'
import {getAuthStateSummary, getCurrentRoute, getRuntimeMode, getServiceWorkerStateSummary} from './appRuntime.js'
import {createRuntimeDebugEventSummary, getRuntimeDebugState, updateRuntimeDebugState} from './debugState.js'

const API_URL = import.meta.env.VITE_API_URL
const MAX_QUEUE_SIZE = 50
const PENDING_SUBMISSION_TTL_MS = 20_000
const QUEUE = []
let flushing = false

function getOriginalFetch() {
  if (typeof window !== 'undefined' && typeof window.__APP_ORIGINAL_FETCH__ === 'function') {
    return window.__APP_ORIGINAL_FETCH__
  }

  return fetch
}

function getAuthorizationHeader() {
  const auth = getAuthData()
  if (!auth?.token || !auth?.expiresAt || auth.expiresAt <= Date.now()) {
    return null
  }

  return `Bearer ${auth.token}`
}

function sanitizeMetadata(metadata = {}) {
  const cleaned = {}
  Object.entries(metadata || {}).forEach(([key, value]) => {
    if (value === undefined) return
    cleaned[key] = value
  })
  return cleaned
}

function enqueue(event) {
  QUEUE.push(event)
  while (QUEUE.length > MAX_QUEUE_SIZE) {
    QUEUE.shift()
  }
}

function getSubmissionTargetName(target = {}) {
  if (!target || typeof target !== 'object') return null

  return (
    target.name ||
    target.id ||
    target.type ||
    target.tagName ||
    null
  )
}

function getFreshPendingSubmission() {
  const pending = getRuntimeDebugState().pendingFormSubmission
  if (!pending) {
    return null
  }

  if ((Date.now() - pending.startedAtMs) > PENDING_SUBMISSION_TTL_MS) {
    updateRuntimeDebugState({pendingFormSubmission: null})
    return null
  }

  return pending
}

export function logRuntimeEvent(type, metadata = {}, {flush = true, consoleOnly = false} = {}) {
  if (!type) return

  const enrichedMetadata = sanitizeMetadata({
    buildVersion: BUILD_VERSION,
    route: getCurrentRoute(),
    runtimeMode: getRuntimeMode(),
    serviceWorkerState: getServiceWorkerStateSummary().state,
    auth: getAuthStateSummary(),
    ...metadata
  })

  try {
    console.info(`[runtime] ${type}`, enrichedMetadata)
  } catch {
    // ignore console errors
  }

  const debugPatch = {
    lastEvent: createRuntimeDebugEventSummary(type, enrichedMetadata),
  }

  if (type === 'request_started') {
    debugPatch.lastRequest = {
      url: enrichedMetadata.url || null,
      method: enrichedMetadata.method || null,
      attempt: enrichedMetadata.attempt || null,
      requestId: enrichedMetadata.requestId || null,
      at: debugPatch.lastEvent.at,
    }
  }

  if (type === 'request_failed' || type === 'request_timeout') {
    debugPatch.lastRequestError = {
      type,
      url: enrichedMetadata.url || null,
      method: enrichedMetadata.method || null,
      status: enrichedMetadata.status || null,
      message: enrichedMetadata.message || null,
      offline: enrichedMetadata.offline === true,
      at: debugPatch.lastEvent.at,
    }
  }

  if (type === 'hard_reload_triggered') {
    debugPatch.lastReload = {
      reason: enrichedMetadata.reason || null,
      at: debugPatch.lastEvent.at,
    }
  }

  if (type === 'hard_reload_suppressed') {
    debugPatch.lastReloadSuppression = {
      reason: enrichedMetadata.reason || null,
      code: enrichedMetadata.code || null,
      at: debugPatch.lastEvent.at,
    }
  }

  updateRuntimeDebugState(debugPatch)

  if (consoleOnly) return

  enqueue({type, metadata: enrichedMetadata})

  if (flush) {
    void flushRuntimeTelemetry()
  }
}

export async function flushRuntimeTelemetry() {
  if (flushing || !QUEUE.length) return
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return

  const authorization = getAuthorizationHeader()
  if (!authorization) return

  flushing = true
  try {
    while (QUEUE.length) {
      const next = QUEUE[0]
      const response = await getOriginalFetch()(`${API_URL}/v2/telemetry`, {
        method: 'POST',
        headers: {
          'Authorization': authorization,
          'Content-Type': 'application/json',
          'X-Bypass-App-Fetch': '1',
          'X-Retry-Safe': 'true'
        },
        body: JSON.stringify(next)
      })

      if (!response.ok) {
        break
      }

      QUEUE.shift()
    }
  } catch {
    // keep queued events for the next flush opportunity
  } finally {
    flushing = false
  }
}

export function markFormSubmitStarted(metadata = {}) {
  const submitId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
  const pending = {
    submitId,
    startedAt: new Date().toISOString(),
    startedAtMs: Date.now(),
    source: metadata.source || 'unknown',
    target: metadata.target || null,
    route: getCurrentRoute(),
  }

  updateRuntimeDebugState({
    pendingFormSubmission: pending,
  })

  logRuntimeEvent('form_submit_started', {
    submitId,
    source: pending.source,
    target: pending.target,
    targetName: getSubmissionTargetName(pending.target),
  }, {flush: false})

  return pending
}

export function resolvePendingFormSubmission({outcome, url, method, status, message} = {}) {
  const pending = getFreshPendingSubmission()
  if (!pending) {
    return null
  }

  updateRuntimeDebugState({
    pendingFormSubmission: null,
  })

  const eventType = outcome === 'success'
    ? 'form_submit_finished'
    : 'form_submit_failed'

  logRuntimeEvent(eventType, {
    submitId: pending.submitId,
    source: pending.source,
    target: pending.target,
    targetName: getSubmissionTargetName(pending.target),
    url: url || null,
    method: method || null,
    status: status || null,
    message: message || null,
    startedAt: pending.startedAt,
  }, {flush: outcome === 'success'})

  return pending
}
